import { useMemo, useState } from "react";
import { useNavigation, useGo } from "@refinedev/core";

import type { Package, PackageLink, PackageType } from "@aos/shared";
import { PACKAGE_TYPES } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoadingState, ErrorState } from "@/components/ui/states";
import {
  EntityGraph,
  typeColor,
  FLOW_PALETTE,
  type EntityGraphNode,
  type EntityGraphEdge,
  type EntityGraphFlow,
} from "@/components/graph";
import { humanize } from "@/lib/format";
import { usePackageGraphData } from "./package-graph-data";

/**
 * Display order of the package strata (left → right of the §11.3 chain). The
 * per-type *colour* is the one sanctioned exception to the black-only accent
 * (design-system §4.6): it is a graph data-signal sourced from the graph's own
 * `typeColor` so the legend dots, the filter swatches, and the rendered nodes
 * all read with one palette. It never leaks into UI chrome.
 */
const STRATA: { type: PackageType; label: string }[] = [
  { type: "principle", label: "Principle" },
  { type: "frame", label: "Frame" },
  { type: "bet", label: "Bet" },
  { type: "contour", label: "Contour" },
  { type: "skill", label: "Skill" },
];

/** Strata rank so the graph columns read Principle → Frame → Bet → Contour → Skill. */
const STRATA_RANK = Object.fromEntries(
  STRATA.map((s, i) => [s.type, i]),
) as Record<PackageType, number>;

/**
 * Package Graph (upd-1 §11.3). A read-only custom page that visualizes every
 * package (skill / contour / bet / frame / principle) as a node and every
 * `package_link` (uses / constrains / shapes / prioritizes / invokes …) as a
 * typed, flow-filterable edge via the shared {@link EntityGraph} (React Flow).
 * A type filter narrows the node set; edges with a missing endpoint are dropped
 * automatically by the dagre layout helper.
 */
export function PackageGraph() {
  const { list } = useNavigation();
  const go = useGo();
  const [typeFilter, setTypeFilter] = useState<PackageType | "all">("all");

  const {
    packages: allPackages,
    links,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = usePackageGraphData();

  const packages = useMemo(
    () =>
      typeFilter === "all"
        ? allPackages
        : allPackages.filter((p) => p.package_type === typeFilter),
    [allPackages, typeFilter],
  );

  const counts = useMemo(() => {
    const map = new Map<PackageType, number>();
    for (const t of PACKAGE_TYPES) map.set(t, 0);
    for (const p of allPackages) {
      map.set(p.package_type, (map.get(p.package_type) ?? 0) + 1);
    }
    return map;
  }, [allPackages]);

  // ----- EntityGraph adapter: packages → nodes, package-links → edges. -----
  const graphNodes = useMemo<EntityGraphNode[]>(
    () =>
      [...packages]
        .sort(
          (a, b) =>
            (STRATA_RANK[a.package_type] ?? 99) -
            (STRATA_RANK[b.package_type] ?? 99),
        )
        .map((p) => ({
          id: p.id,
          title: p.name,
          type: p.package_type,
          subtitle: `${humanize(p.package_type)} · ${humanize(p.status)}`,
          description: p.short_summary ?? p.description ?? undefined,
          cluster: p.package_type,
          tag: p.maturity_stage ?? undefined,
          path: `/${resourceForType(p.package_type)}/${p.id}`,
          meta: { resource: resourceForType(p.package_type) },
        })),
    [packages],
  );

  const graphEdges = useMemo<EntityGraphEdge[]>(
    () =>
      links.map((l) => ({
        id: l.id,
        from: l.source_package_id,
        to: l.target_package_id,
        label: l.link_type,
        flow: l.link_type,
        dashed: l.link_type === "constrains",
      })),
    [links],
  );

  const graphFlows = useMemo<EntityGraphFlow[]>(() => {
    const seen: string[] = [];
    for (const l of links) {
      if (!seen.includes(l.link_type)) seen.push(l.link_type);
    }
    return seen.map((linkType, i) => ({
      id: linkType,
      label: humanize(linkType),
      color: FLOW_PALETTE[i % FLOW_PALETTE.length] ?? FLOW_PALETTE[0] ?? "#334155",
    }));
  }, [links]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Package Graph"
        subtitle="Packages as nodes and typed package links as edges — Principle → Frame → Bet → Contour → Skill."
        icon="graph"
        actions={
          <Button variant="outline" size="sm" onClick={() => list("packages")}>
            <Icon name="packages" size={16} className="mr-2" aria-hidden />
            Package list
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Principle → Frame → Bet → Contour → Skill</CardTitle>
          <CardDescription>
            {allPackages.length} package
            {allPackages.length === 1 ? "" : "s"} · {links.length} link
            {links.length === 1 ? "" : "s"}. Click a node to open its detail.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={typeFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setTypeFilter("all")}
            >
              All ({allPackages.length})
            </Button>
            {STRATA.map(({ type, label }) => (
              <Button
                key={type}
                variant={typeFilter === type ? "default" : "outline"}
                size="sm"
                onClick={() => setTypeFilter(type)}
              >
                <span
                  className="mr-2 inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: typeColor(type) }}
                  aria-hidden
                />
                {label} ({counts.get(type) ?? 0})
              </Button>
            ))}
          </div>

          {loading ? (
            <LoadingState label="Loading graph…" rows={6} />
          ) : isError ? (
            <ErrorState
              error={error}
              onRetry={() => {
                refetch();
              }}
            />
          ) : (
            <EntityGraph
              nodes={graphNodes}
              edges={graphEdges}
              flows={graphFlows}
              direction="LR"
              height="70vh"
              onNodeClick={(node) => {
                if (node.path) go({ to: node.path });
              }}
              emptyMessage={
                typeFilter === "all"
                  ? "No packages yet."
                  : `No ${typeFilter} packages yet.`
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Map a package type to the Refine resource its detail page lives under. Bets /
 * frames / principles have dedicated typed views; skills / contours route to
 * the existing facade-backed resources; everything else falls back to the
 * generic `packages` resource.
 */
function resourceForType(type: PackageType): string {
  switch (type) {
    case "bet":
      return "bets";
    case "frame":
      return "frames";
    case "principle":
      return "principles";
    case "skill":
      return "skills";
    case "contour":
      return "processes";
    default:
      return "packages";
  }
}

export default PackageGraph;
